export interface ITPIntegrationCredentials {
  clientId: string;
  secret: string;
}

export interface ITPIntegrationCredentialsResponse {
  id: string;
  clientId: string;
  tenantId: string;
}

export class ITPIntegrationCredentialSdk {
  private baseURL: string;

  /**
   * Constructor
   * @param {string} baseURL - The base URL for the service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseURL = baseURL;
  }

  /**
   * Wrapper for handling fetch requests and error handling
   * @param {string} endpoint - API endpoint
   * @param {RequestInit} [options] - Request options
   * @returns {Promise<Response>} - Response or throws an error
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<Response> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage += `, message: ${errorData.message}`;
        }
      } catch (e) {
        // ignore
      }
      throw new Error(errorMessage);
    }

    return response;
  }

  /**
   * Get ITP Integration Credentials for tenant
   * @param {string} accessToken - user's access token
   * @returns {Promise<ITPIntegrationCredentialsResponse>} - Credentials for tenant
   */
  async getITPIntegrationCredentials(
    accessToken: string
  ): Promise<ITPIntegrationCredentialsResponse> {
    const endpoint = `/itp/integration/credentials`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Create new ITP Integration Credentials
   * @param {string} accessToken - user's access token
   * @param {ITPIntegrationCredentials} data - credentials data to create
   * @returns {Promise<void>} - Created successfully (201)
   */
  async addITPIntegrationCredentials(
    accessToken: string,
    data: ITPIntegrationCredentials
  ): Promise<void> {
    const endpoint = `/itp/integration/credentials`;

    await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  /**
   * Update existing ITP Integration Credentials
   * @param {string} accessToken - user's access token
   * @param {string} id - credential ID to update
   * @param {ITPIntegrationCredentials} data - credentials data to update
   * @returns {Promise<ITPIntegrationCredentials>} - Updated credentials
   */
  async updateITPIntegrationCredentials(
    accessToken: string,
    id: string,
    data: ITPIntegrationCredentials
  ): Promise<ITPIntegrationCredentials> {
    const endpoint = `/itp/integration/credentials/${id}`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  /**
   * Delete ITP Integration Credentials
   * @param {string} accessToken - user's access token
   * @param {string} id - credential ID to delete
   * @returns {Promise<string>} - Deletion confirmation message
   */
  async deleteITPIntegrationCredentials(accessToken: string, id: string): Promise<string> {
    const endpoint = `/itp/integration/credentials/${id}`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.text();
  }
}
